import React, { PropsWithChildren, useEffect, useCallback } from "react";
import { useAuth } from "react-oidc-context";

/**
 * Usage:
 * <RequireAuth>
 *   <YourComponent />
 * </RequireAuth>
 */

const RequireAuth: React.FC<PropsWithChildren> = ({ children }) => {
  const auth = useAuth();

  const handleSignIn = useCallback(() => {
    // Don't redirect if we're in the middle of processing a callback
    if (!auth.isLoading && !auth.isAuthenticated) {
      // Check if we're on a callback URL (has code parameter)
      const urlParams = new URLSearchParams(window.location.search);
      const hasCode = urlParams.has("code");

      if (!hasCode) {
        auth.signinRedirect();
      }
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.signinRedirect]);

  useEffect(() => {
    handleSignIn();
  }, [handleSignIn]);

  // Show loading while processing authentication
  if (auth.isLoading) return <div>Loading...</div>;

  // Show error if authentication failed
  if (auth.error) return <div>Error: {auth.error.message}</div>;

  // Show loading while redirecting to login
  if (!auth.isAuthenticated || !auth.user) {
    // Check if we're on a callback URL
    const urlParams = new URLSearchParams(window.location.search);
    const hasCode = urlParams.has("code");

    if (hasCode) {
      return <div>Processing authentication...</div>;
    }

    return <div>Redirecting to login...</div>;
  }

  return <>{children}</>;
};

export default RequireAuth;
